import { inject, Injectable } from '@angular/core';
import { IEventTelemetry, IExceptionTelemetry } from '@microsoft/applicationinsights-common';
import { ICustomProperties } from '@microsoft/applicationinsights-core-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { makeContextProps, normalizeError } from '../logging/utils';
import { APPLICATION_INSIGHTS_INSTRUMENTATION_TOKEN } from '../tokens/application-insights-intrumentation-key.token';

@Injectable()
export class ApplicationInsightService {
  private readonly instrumentationKey = inject(APPLICATION_INSIGHTS_INSTRUMENTATION_TOKEN);
  private readonly appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: this.instrumentationKey,
      enableAutoRouteTracking: true,
    },
  });

  public constructor() {
    this.appInsights.loadAppInsights();
    this.appInsights.trackPageView();

    window.addEventListener('unhandledrejection', (event) => {
      // event.reason can be Error, string, object, etc.
      const reason =
        event.reason instanceof Error
          ? event.reason
          : new Error(typeof event.reason === 'string' ? event.reason : JSON.stringify(event.reason));

      this.appInsights.trackException({
        exception: reason,
        properties: {
          label: 'svh-configurator',
          source: 'unhandledrejection',
          href: location.href,
          userAgent: navigator.userAgent,
        },
      });
    });

    window.addEventListener('error', (event) => {
      const err = (event as ErrorEvent).error;
      const exception = err instanceof Error ? err : new Error((event as ErrorEvent).message);

      this.appInsights.trackException({
        exception,
        properties: {
          label: 'svh-configurator',
          source: 'window.error',
          href: location.href,
          userAgent: navigator.userAgent,
        },
      });
    });
  }

  public logError(err: unknown, extra?: Record<string, any>) {
    const e = normalizeError(err);

    this.appInsights.trackException({
      exception: new Error(e.message),
      properties: makeContextProps({
        errorName: e.name,
        stack: e.stack,
        isHttp: String(!!e.isHttp),
        status: e.status ? String(e.status) : undefined,
        ...extra,
      }),
    });
  }

  public trackPageView() {
    this.appInsights.trackPageView();
  }

  public trackEvent(event: IEventTelemetry, customProperties?: ICustomProperties) {
    this.appInsights.trackEvent(event, customProperties);
  }

  public trackException(exception: IExceptionTelemetry, customProperties?: ICustomProperties) {
    this.appInsights.trackException(exception, customProperties);
  }
}
